<template>
  <div :class="$style.chatMessageReview">
    <p :class="$style.chatMessageReview__message" v-if="isRequested">
      {{ props.user.nickname }}さんにチャットの評価を依頼しました。
    </p>
    <template v-if="isScored || isCommented">
      <p :class="$style.chatMessageReview__message">
        {{ props.user.nickname }}さんが評価を送信しました。
      </p>
      <MessageItem
        v-if="review"
        :class="$style.chatMessageReview__review"
        :date="props.message.createdAt"
        :personalityType="personalityType"
        userType="user"
        @clickUserThumbnail="emit('clickUserThumbnail')"
      >
        <section :class="$style.review">
          <p :class="$style.review__reviewer">
            {{ props.user.nickname }}さんからの評価
          </p>
          <p :class="[$style.review__score, $style.score]">
            <RatingStars
              :class="$style.score__stars"
              :max="5"
              :rate="review.score"
              :size="30"
              :gap="8"
            >
              <template #rate>
                <span :class="$style.score__rating">
                  {{ fixedScore }}
                </span>
              </template>
            </RatingStars>
          </p>
          <p
            v-if="isCommented && review.review !== ''"
            :class="$style.review__comment"
          >
            {{ review.review }}
          </p>
        </section>
      </MessageItem>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import RatingStars from '@/components/molecules/RatingStars.vue'
import MessageItem from '@/components/molecules/MessageItem.vue'
import type { Match, PersonalityType, ReviewMessage } from '@/types/app'
import { useChatMessageReviewQuery } from './ChatMessageReview.generated'

const props = defineProps<{
  user: Match['user']
  message: ReviewMessage
}>()

const emit = defineEmits<{
  (e: 'clickUserThumbnail'): void
}>()

const { result } = useChatMessageReviewQuery({
  userId: String(props.user.id),
})

const review = computed(() => result.value?.review)

const isRequested = computed(() => review.value?.status === 'requested')
const isScored = computed(() => review.value?.status === 'scored')
const isCommented = computed(() => review.value?.status === 'commented')
const personalityType = computed<Maybe<PersonalityType>>(
  () => props.user.diagnosisResult?.personality.type
)
const fixedScore = computed(() =>
  Number.parseFloat(`${review.value?.score}`).toFixed(1)
)
</script>

<style lang="scss" module>
.chatMessageReview {
  &__message {
    padding: 16px;
    background-color: $color-surface-tertiary;
    border-radius: $border-radius-v2-rounder;
    box-shadow: $shadow-24;
    color: $color-text-and-object-high-emphasis;
    font-size: 16px;
    font-weight: bold;
    text-align: center;

    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__review {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.review {
  padding: 24px;

  &__reviewer {
    color: $color-text-and-object-high-emphasis;
    font-size: 18px;
    font-weight: bold;
  }

  &__score {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__comment {
    color: $color-text-and-object-high-emphasis;
    font-size: 14px;
    font-weight: normal;
    line-height: 24px;

    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.score {
  display: flex;
  align-items: center;

  &__rating {
    color: $color-text-and-object-accent-orange;
    font-size: 18px;
    font-weight: bold;

    &:nth-child(n + 2) {
      margin-left: 16px;
    }
  }
}
</style>
